import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useWorkspace } from '../contexts/WorkspaceContext';
import apiClient from '../config/axios';
import { X, MessageSquare } from 'lucide-react';

/**
 * Nudges workspace owners/admins to configure Meta WhatsApp API credentials.
 * Dismissible per workspace (localStorage).
 */
const WhatsAppSetupBanner = () => {
  const { currentWorkspace } = useWorkspace();
  const role = String(currentWorkspace?.user_role || '').toLowerCase();
  const isOwnerOrAdmin = role === 'owner' || role === 'admin';
  const wsId = currentWorkspace?.id;
  const storageKey = wsId != null ? `whatsappSetupDismissed:${wsId}` : null;

  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (storageKey && typeof window !== 'undefined') {
      setDismissed(localStorage.getItem(storageKey) === '1');
    }
  }, [storageKey]);

  const { data, isLoading } = useQuery(
    ['whatsappSetupStatus', wsId],
    async () => {
      const res = await apiClient.get('/api/settings/whatsapp', {
        headers: { 'x-workspace-id': String(wsId) }
      });
      return res.data;
    },
    {
      enabled: Boolean(wsId) && isOwnerOrAdmin && !dismissed,
      staleTime: 60 * 1000,
      retry: 1
    }
  );

  const needsSetup = useMemo(() => {
    const s = data?.settings;
    if (!s) return true;
    const phoneId = String(s.phone_number_id || '').trim();
    const token = String(s.access_token || '').trim();
    return !phoneId || !token;
  }, [data]);

  const dismiss = () => {
    if (storageKey) {
      localStorage.setItem(storageKey, '1');
    }
    setDismissed(true);
  };

  if (!wsId || !isOwnerOrAdmin || dismissed) return null;
  if (isLoading) return null;
  if (!needsSetup) return null;

  return (
    <div className="bg-sky-50 border-b border-sky-200 px-4 py-3">
      <div className="max-w-6xl mx-auto flex items-start sm:items-center justify-center gap-3 flex-wrap text-center sm:text-left">
        <MessageSquare className="w-5 h-5 text-sky-700 shrink-0 mt-0.5" />
        <p className="text-sm text-sky-900 flex-1 min-w-0">
          <span className="font-semibold">Connect WhatsApp Business API</span>
          <span className="text-sky-800">
            {' '}
            — Use Meta Embedded Signup or add credentials manually in Settings to send and receive messages.
          </span>
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/setup/whatsapp"
            className="inline-flex items-center px-3 py-1.5 text-sm font-semibold text-white bg-sky-700 hover:bg-sky-800 rounded-lg transition-colors"
          >
            Connect with Meta
          </Link>
          <Link
            to="/settings?tab=whatsapp"
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-sky-900 bg-white border border-sky-300 hover:bg-sky-50 rounded-lg transition-colors"
          >
            Manual setup
          </Link>
          <button
            type="button"
            onClick={dismiss}
            className="p-1.5 rounded-lg text-sky-700 hover:bg-sky-100 transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppSetupBanner;
